/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable no-bitwise */

import Numeric from './numeric/numeric';
import Stringify from './stringify';

/**
 * @typedef {never
 *   |  0 |  1 |  2 |  3 |  4 |  5 |  6 |  7 |  8 |  9
 *   | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19
 *   | 20 | 21 | 22 | 23 | 24 | 25 | 26 | 27 | 28 | 29
 *   | 30 | 31
 * } BitfieldIndices
 * @typedef {Exclude<BitfieldIndices | 32, 0>} BitfieldSize
 */

// TODO: Vyřešit otypování konstruktorů.
// - https://www.typescriptlang.org/docs/handbook/advanced-types.html#polymorphic-this-types
// - https://github.com/Microsoft/TypeScript/issues/3841
// - https://stackoverflow.com/questions/55866473/infer-constructor-type-in-typescript#comment98399515_55867277
/**
 * @template {BitfieldIndices} T
 * @template {Bitfield<T>} B
 * @typedef {{ new (o: B): B }} BitfieldConstructor
 */

/**
 * Generické bitové pole pro souvislé [0, n) číselné typy.
 *
 * Tato třída ctí immutable principy a je pouze read-only.
 * @template {BitfieldIndices} T Souvislý číselný typ v rozsahu [0, n).
 */
export default class Bitfield {
  /**
   * Vytvoří nové bitové pole ze zadaného počtu bitů a čísla.
   * @protected
   * @param {Object} o
   * @param {BitfieldSize} o.n Počet bitů bitového pole v rozsahu (0, 32].
   * @param {number} o.bitfield Číslo představující bitové pole v rozsahu [0, 2^n).
   * @throws {RangeError} Pokud je {@link n} nebo {@link bitfield} mimo povolený rozsah.
   */
  constructor({ n, bitfield }) {
    // TODO #555: Možná některé runtime kontroly nejsou potřeba díky přesnějším
    // typům, např. nyní `n`.
    // if (!Numeric.isIndex(n) || n === 0 || n > 32) {
    //   throw new RangeError(`n=${n}, but 0 < n <= 32`);
    // }
    const maxBitfield = 2 ** n - 1;
    if (!(Numeric.isIndex(bitfield) && bitfield <= maxBitfield)) {
      throw new RangeError(
        `bitfield=${bitfield}, but 0 <= bitfield <= ${maxBitfield}`
      );
    }
    /** @readonly */
    this.n = n;
    /** @readonly */
    this.bitfield = bitfield >>> 0;

    if (new.target === Bitfield) {
      Object.freeze(this);
    }
  }

  /**
   * Vytvoří nové bitové pole ze zadaného počtu bitů a čísla.
   * @template {BitfieldIndices} T Souvislý číselný typ v rozsahu [0, n).
   * @param {BitfieldSize} n Počet bitů bitového pole v rozsahu (0, 32].
   * @param {number} bitfield Číslo představující bitové pole v rozsahu [0, 2^n).
   * @returns {Bitfield<T>}
   * @throws {RangeError} Pokud je {@link n} nebo {@link bitfield} mimo povolený rozsah.
   */
  static bitfield(n, bitfield) {
    return new Bitfield({ n, bitfield });
  }

  /**
   * Vrátí příznak, zda jsou všechny bity nenastavené.
   * @returns {boolean}
   */
  empty() {
    return this.bitfield === 0;
  }

  // TODO: Chybí kontrola na přístup mimo rozsah, ale to by měl teoreticky TS podchytit.
  /**
   * Vrátí příznak, zda je bit na daném indexu nastaven.
   * @param {T} index
   * @returns {boolean}
   */
  get(index) {
    return (this.bitfield & (1 << index)) !== 0;
  }

  inspect() {
    return this.toString();
  }

  /**
   * Vrátí seznam indexů nastavených bitů.
   * @returns {T[]}
   */
  toIndices() {
    /** @type {T[]} */
    const indices = [];
    let i = 0;
    let m = this.bitfield;
    while (m) {
      if ((m & 1) !== 0) {
        indices.push(/** @type {T} */ (i));
      }
      i += 1;
      m >>>= 1;
    }
    return indices;
  }

  /**
   * Vrátí textovou reprezentaci.
   * @returns {string}
   */
  toString() {
    return `${this.bitfield
      .toString(2)
      .padStart(this.n, '0')} ${Stringify.value(this.toIndices())}`;
  }

  /**
   * Vrátí sjednocení (logický součet) bitového pole s jiným o stejném počtu bitů.
   * @param {this} other
   * @returns {this}
   * @throws {TypeError} Pokud je other nekompatibilní (jiná bitová šířka) s this.
   */
  union(other) {
    if (this.n !== other.n) {
      throw new TypeError(`incompatible n: ${this.n} !== ${other.n}`);
    }
    return new /** @type {BitfieldConstructor<T, this>} */ (this.constructor)({
      ...this,
      bitfield: this.bitfield | other.bitfield
    });
  }
}

/**
 * Vypočte masku na základě zadaných indexů.
 * @template {BitfieldIndices} T Souvislý číselný typ v rozsahu [0, 32).
 * @param  {...T} indices
 * @returns {number}
 */
export function mask(...indices) {
  return indices.reduce((acc, i) => (acc | (1 << i)) >>> 0, 0);
}

export const internal = {
  Bitfield
};
